/**
 * The user-facing text for a refusal from the guest-invite endpoints (plan 4, Contract notes 3).
 *
 * The API store throws tcx-api's error object (status, code, path, inner). Each action has its own refusals: a create
 * can be forbidden (guest calls off, or a host the caller may not invite for), name an unknown host, fail validation on
 * one field (422) or hit the 100-invite cap (409); a revoke or resend can come too late for an invite that expired or
 * ended (409); the "all invites" list is for managers only (403). Everything else is a generic "try again".
 */

import _i18n from '@/utilities/I18n'
import type { ToastMessage } from '@/definitions/Common'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'

const i18n = _i18n.global

export type GuestInviteAction = 'create' | 'revoke' | 'resend' | 'listAll'

/** The create-body fields a 422 can point at - each has a guest.field.<name> text */
const FIELDS = ['host', 'guestName', 'sms', 'email', 'message', 'notBefore', 'expiresAt', 'language'] as const

export type GuestInviteField = typeof FIELDS[number]

/**
 * The field a 422 points at: the error's own path, else the first inner error's - the last segment, so 'body.sms' is 'sms'
 * @param err The API error
 * @returns The field, undefined when the error names none we know
 */
export function invalidField(err: Partial<ErrorResponseDetail>): GuestInviteField | undefined {
	const paths = [err.path, ...(err.inner ?? []).map(inner => inner.path)]

	for (const path of paths) {
		const name = path?.split('.').pop()

		if (name && (FIELDS as readonly string[]).includes(name)) { return name as GuestInviteField }
	}
	return undefined
}

/**
 * The toast for a refused guest-invite request
 * @param action What was refused
 * @param err What the API store threw
 */
export function guestInviteErrorToast(action: GuestInviteAction, err: unknown): ToastMessage {
	const error = (err ?? {}) as Partial<ErrorResponseDetail>
	const status = typeof error.status === 'number' ? error.status : undefined
	const summary = i18n.t('guest.errorHeader')
	const warn = (detail: string): ToastMessage => ({ severity: 'warn', summary, detail })

	if (status === 0) { return { severity: 'error', summary, detail: i18n.t('common.error.commErrorMessage') } }

	switch (action) {
		case 'create': {
			if (status === 403) { return warn(i18n.t('guest.errorCreateForbidden')) }
			if (status === 404) { return warn(i18n.t('guest.errorUnknownHost')) }
			if (status === 409) { return warn(i18n.t('guest.errorCapReached')) }
			if (status === 422) {
				const field = invalidField(error)

				if (field === 'host') { return warn(i18n.t('guest.errorHostNoExtension')) }
				return warn(field ? i18n.t('guest.errorInvalidField', { field: i18n.t(`guest.field.${field}`) }) : i18n.t('guest.errorInvalid'))
			}
			break
		}
		case 'revoke':
			if (status === 409) { return { severity: 'info', summary, detail: i18n.t('guest.errorRevokeExpired') } }
			break
		case 'resend':
			if (status === 409) { return { severity: 'info', summary, detail: i18n.t('guest.errorResendNotActive') } }
			break
		case 'listAll':
			if (status === 403) { return { severity: 'info', summary, detail: i18n.t('guest.errorAllForbidden') } }
			break
	}

	return { severity: 'error', summary, detail: i18n.t('guest.errorGeneric') }
}
